import { useEffect } from "react"
import { router } from "@inertiajs/react"

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

const SCRIPT_ID = "ga4-gtag"

/**
 * Google Analytics 4, mounted from MarketingLayout so it can only ever run on
 * the public site.
 *
 * That placement is the point. There's one `application.html.erb` for the whole
 * app, so a tag in `<head>` would fire on driver and admin pages too — and
 * those paths carry things like `/packet/sign/4821`. In a product holding Act
 * 24 and child-abuse records, sending those to Google is not a tidiness
 * problem. Scoping it to the marketing layout makes the boundary structural
 * rather than a rule someone has to remember when adding a route.
 *
 * Does nothing without a measurement id, which is how development and test stay
 * out of the property.
 */
export function useGoogleAnalytics(measurementId?: string | null) {
  useEffect(() => {
    if (!measurementId) return

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement("script")
      script.id = SCRIPT_ID
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
      document.head.appendChild(script)

      window.dataLayer = window.dataLayer ?? []
      // gtag reads its own `arguments` object; pushing a plain array instead is
      // not equivalent, which is why this can't be an arrow function.
      window.gtag = function () {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer?.push(arguments)
      } as (...args: unknown[]) => void

      window.gtag("js", new Date())
      // send_page_view: false, because Inertia visits never reload the document.
      // gtag's automatic view fires once at boot and then never again, so every
      // page after the first would be invisible. We send them ourselves below.
      window.gtag("config", measurementId, { send_page_view: false })
    }

    // Inertia fires `navigate` for the first page as well as later visits, but
    // whether it lands before or after this effect registers is a race we don't
    // control. Sending on mount *and* listening covers both orderings; skipping
    // a repeat of the same path stops the two from double-counting every
    // landing page, which is what they did.
    let lastPath: string | null = null
    const sendPageView = () => {
      const path = window.location.pathname + window.location.search
      if (path === lastPath) return
      lastPath = path

      window.gtag?.("event", "page_view", {
        page_location: window.location.href,
        page_path: path,
        page_title: document.title
      })
    }

    sendPageView() // the landing page
    return router.on("navigate", sendPageView) // and every visit after it
  }, [measurementId])
}
